/**
 * Procedural grey-box map data for development (Phaser-free). `makeDevMap` is deterministic for a
 * given (width, height, seed) and exercises every MapData feature: three layer bands, collision,
 * walk-in exits + a confirm door (`kind: 'exit'` interaction ↔ same-id exit), spawns, gated / once /
 * auto interactions, an NPC placement, lights and water/shelter zones.
 * Tile indices refer to the dev tileset drawn by `devkit.ensureDevTileset` (same order as DEV_TILE_NAMES).
 */
import { TILE } from '@/core/config';
import { SeededRng } from '@/core/rng';
import type { MapData, MapExit, MapInteraction, MapSpawn } from '@/core/types/contracts';

export const DEV_TILESET_ID = 'dev-tiles';

export const DevTile = {
  SAND: 0, EARTH: 1, PLANK: 2, WATER: 3, WALL: 4, TREE: 5, CRATE: 6, GRASS: 7, CANOPY: 8, ROOF: 9,
} as const;
export type DevTileId = (typeof DevTile)[keyof typeof DevTile];
export const DEV_TILE_NAMES = ['sand', 'earth', 'plank', 'water', 'wall', 'tree', 'crate', 'grass', 'canopy', 'roof'] as const;
/** Tiles that block movement when present in ground/detail layers. */
export const DEV_SOLID_TILES: ReadonlySet<number> = new Set<number>([DevTile.WATER, DevTile.WALL, DevTile.TREE, DevTile.CRATE]);

export interface DevMapOptions {
  id?: string;
  /** Target map id for the east exit (default 'dev-next'). */
  exitTarget?: string;
  /** Story flag ids used by the gated sample interactions. */
  gateFlag?: string;
}

export interface DevMapLayout {
  roadRow: number; waterRow: number; pierCol: number;
  building: { tx: number; ty: number; tw: number; th: number };
  /** Gap in the building's bottom wall: walk-in exit `door` + confirm interaction `door` (kind 'exit'). */
  door: { tx: number; ty: number };
  trees: { tx: number; ty: number }[]; crates: { tx: number; ty: number }[]; well: { tx: number; ty: number };
}

const EMPTY = -1;

/** Builds a deterministic dev map. Minimum size 12×10 tiles (smaller requests are clamped). */
export function makeDevMap(width = 30, height = 20, seed: number | string = 1157, opts: DevMapOptions = {}): MapData & { layout: DevMapLayout } {
  const w = Math.max(12, Math.floor(width)), h = Math.max(10, Math.floor(height));
  const rng = new SeededRng(typeof seed === 'string' ? seed : seed >>> 0);
  const id = opts.id ?? `dev-${w}x${h}-${seed}`;
  const exitTarget = opts.exitTarget ?? 'dev-next';
  const gateFlag = opts.gateFlag ?? 'HARBOR_FREE_ROAM';

  const ground = new Array<number>(w * h).fill(DevTile.SAND);
  const detail = new Array<number>(w * h).fill(EMPTY);
  const overhead = new Array<number>(w * h).fill(EMPTY);
  const idx = (tx: number, ty: number) => ty * w + tx;
  const inb = (tx: number, ty: number) => tx >= 0 && ty >= 0 && tx < w && ty < h;

  const roadRow = Math.floor(h / 2) - 1;       // road occupies roadRow and roadRow + 1
  const waterRow = h - 3;                      // rows waterRow..h-1 are water
  const pierCol = Math.floor(w * 0.7);
  const building = { tx: 2, ty: 1, tw: 5, th: 3 };
  const door = { tx: building.tx + building.tw - 2, ty: building.ty + building.th - 1 };
  const well = { tx: w - 10, ty: roadRow + 3 };
  const crates = [{ tx: w - 6, ty: roadRow - 2 }, { tx: w - 5, ty: roadRow - 2 }, { tx: w - 6, ty: roadRow - 3 }];

  // ground: road, shoreline water, open sea (empty ground → the layered background shows through), pier
  for (let tx = 0; tx < w; tx++) { ground[idx(tx, roadRow)] = DevTile.EARTH; ground[idx(tx, roadRow + 1)] = DevTile.EARTH; }
  for (let ty = waterRow; ty < h; ty++) for (let tx = 0; tx < w; tx++) ground[idx(tx, ty)] = ty === waterRow ? DevTile.WATER : EMPTY;
  for (let ty = waterRow - 2; ty < h; ty++) ground[idx(pierCol, ty)] = DevTile.PLANK;

  // cosmetic grass patches on sand (deterministic)
  for (let ty = 0; ty < waterRow - 2; ty++) for (let tx = 0; tx < w; tx++) {
    if (ground[idx(tx, ty)] === DevTile.SAND && rng.chance(0.08)) ground[idx(tx, ty)] = DevTile.GRASS;
  }

  // building walls (detail, solid) with a doorway gap in the bottom wall (earth floor, walkable)
  for (let ty = building.ty; ty < building.ty + building.th; ty++) for (let tx = building.tx; tx < building.tx + building.tw; tx++) detail[idx(tx, ty)] = DevTile.WALL;
  detail[idx(door.tx, door.ty)] = EMPTY;
  ground[idx(door.tx, door.ty)] = DevTile.EARTH;

  // reserved cells: road ± 1, water, pier column, building + 1 margin
  const reserved = (tx: number, ty: number): boolean => {
    if (!inb(tx, ty)) return true;
    if (ty >= roadRow - 1 && ty <= roadRow + 2) return true;
    if (ty >= waterRow - 3) return true;
    if (tx === pierCol) return true;
    if (tx >= building.tx - 1 && tx < building.tx + building.tw + 1 && ty >= building.ty - 1 && ty < building.ty + building.th + 1) return true;
    if (detail[idx(tx, ty)] !== EMPTY) return true;
    return false;
  };

  // crates + well (detail, solid)
  for (const cr of crates) if (inb(cr.tx, cr.ty)) detail[idx(cr.tx, cr.ty)] = DevTile.CRATE;
  if (inb(well.tx, well.ty)) detail[idx(well.tx, well.ty)] = DevTile.CRATE;

  // trees: trunk in detail (solid), canopy in overhead one tile above (walk-under)
  const trees: { tx: number; ty: number }[] = [];
  const target = Math.max(3, Math.floor((w * h) / 45));
  for (let attempt = 0; attempt < target * 20 && trees.length < target; attempt++) {
    const tx = rng.int(1, w - 2), ty = rng.int(1, h - 1);
    if (reserved(tx, ty)) continue;
    if (trees.some((t) => Math.abs(t.tx - tx) <= 1 && Math.abs(t.ty - ty) <= 1)) continue;
    if (overhead[idx(tx, ty)] !== EMPTY) continue;
    trees.push({ tx, ty });
    detail[idx(tx, ty)] = DevTile.TREE;
    overhead[idx(tx, ty - 1)] = DevTile.CANOPY;
  }

  // collision: solid tiles and void ground (open sea) block; planks over the sea stay walkable
  const collision = new Array<number>(w * h).fill(0);
  for (let i = 0; i < w * h; i++) {
    const g = ground[i] as number, d = detail[i] as number;
    if (g < 0 || DEV_SOLID_TILES.has(g) || DEV_SOLID_TILES.has(d)) collision[i] = 1;
  }

  const px = (tx: number) => tx * TILE + TILE / 2;
  const py = (ty: number) => ty * TILE + TILE - 6;
  const spawns: MapSpawn[] = [
    { id: 'start', x: px(3), y: py(roadRow + 1), facing: 'right' },
    { id: 'west', x: px(1), y: py(roadRow + 1), facing: 'right' },
    { id: 'east', x: px(w - 3), y: py(roadRow + 1), facing: 'left' },
    { id: 'pier', x: px(pierCol), y: py(waterRow - 2), facing: 'down' },
  ];
  const exits: MapExit[] = [
    { id: 'east', x: (w - 1) * TILE, y: roadRow * TILE, w: TILE, h: TILE * 2, target: exitTarget, spawn: 'west' },
    { id: 'pier-end', x: pierCol * TILE, y: (h - 1) * TILE, w: TILE, h: TILE, target: 'dev-boat', spawn: 'deck' },
    { id: 'door', x: door.tx * TILE, y: door.ty * TILE, w: TILE, h: TILE, target: 'dev-inn', spawn: 'entrance' },
  ];
  const npcTile = { tx: 10, ty: roadRow - 1 };
  const firstTree = trees[0] ?? { tx: 1, ty: 1 };
  const interactions: MapInteraction[] = [
    { id: 'npc-greeter', kind: 'talk', x: npcTile.tx * TILE, y: npcTile.ty * TILE, w: TILE, h: TILE, dialogue: 'talk.dev.greeter', label: '交谈' },
    { id: 'crate-candles', kind: 'observe', x: (w - 6) * TILE, y: (roadRow - 2) * TILE, w: TILE * 2, h: TILE, dialogue: 'obs.dev.crates', label: '查看' },
    { id: 'tree-old', kind: 'observe', x: firstTree.tx * TILE, y: firstTree.ty * TILE, w: TILE, h: TILE, dialogue: 'obs.dev.tree', label: '查看', once: true },
    { id: 'road-midpoint', kind: 'trigger', x: Math.floor(w / 2) * TILE, y: roadRow * TILE, w: TILE, h: TILE * 2, cinematic: 'cine.dev.road', auto: true, once: true },
    { id: 'well-locked', kind: 'trigger', x: well.tx * TILE, y: well.ty * TILE, w: TILE, h: TILE, cinematic: 'cine.dev.well', label: '打水', requireFlag: gateFlag },
    { id: 'notice-board', kind: 'observe', x: building.tx * TILE, y: (building.ty + building.th - 1) * TILE, w: TILE * 2, h: TILE, dialogue: 'obs.dev.notice', label: '查看', untilFlag: gateFlag },
    { id: 'pier-view', kind: 'observe', x: pierCol * TILE, y: (h - 2) * TILE, w: TILE, h: TILE, dialogue: 'obs.dev.sea', label: '眺望' },
    // confirm door: resolves to the exit with the same id (walking into the doorway also fires the exit edge-triggered)
    { id: 'door', kind: 'exit', x: door.tx * TILE, y: door.ty * TILE, w: TILE, h: TILE, label: '进门' },
  ];

  const data: MapData & { layout: DevMapLayout } = {
    id,
    width: w, height: h, tileSize: 32,
    layers: [
      { name: 'ground', tileset: DEV_TILESET_ID, data: ground, depth: 'ground' },
      { name: 'detail', tileset: DEV_TILESET_ID, data: detail, depth: 'detail' },
      { name: 'overhead', tileset: DEV_TILESET_ID, data: overhead, depth: 'overhead' },
    ],
    collision,
    exits,
    spawns,
    interactions,
    npcs: [{ id: 'dev-npc', actor: 'dev-npc', x: px(npcTile.tx), y: py(npcTile.ty), facing: 'down', dialogue: 'talk.dev.greeter' }],
    lights: [{ x: (building.tx + building.tw) * TILE, y: (building.ty + building.th) * TILE, radius: 64, color: '#ffb347', intensity: 0.8, flicker: true }],
    water: [{ x: 0, y: waterRow * TILE, w: w * TILE, h: (h - waterRow) * TILE }],
    rainShelter: [{ x: building.tx * TILE, y: building.ty * TILE, w: building.tw * TILE, h: building.th * TILE }],
    layout: { roadRow, waterRow, pierCol, building, door, trees, crates, well },
  };
  return data;
}
